@import '../../utils/global.module.less';

@fl_border: #eceef2;
@fl_divider: #eaecf0;
@fl_text: #475467;
@fl_text_muted: #98a2b3;
@fl_tile_title: #323b48;
@fl_btn_border: #e3e6eb;
@fl_tile_hover_bg: #ddebff;
@fl_btn_hover_bg: #f1f7fe;
@fl_btn_hover_border: #9dbffd;

// Left column of the unified Case Files container: the right edge stays square
// so it butts up against the file viewer with no seam.
.round_border_container {
    background-color: #ffffff;
    border: 1px solid @fl_divider;
    border-radius: 12px 0 0 12px;
    box-shadow: 0 1px 2px rgba(16, 24, 40, 0.06);
    overflow: hidden;
}

.card_header {
    background-color: @title-color;
    border-radius: 12px 0 0 0;
    align-items: center;
    gap: 16px;
    padding: 0 8px 0 16px;
    width: 100%;
    // Matches the Adobe PDF viewer's toolbar height (46.2px) so both headers
    // line up as one bar across the unified container.
    height: 46.2px;
    flex-shrink: 0;
}

.card_header_title {
    flex: 1 0 0;
    min-width: 0;
    display: flex;
    align-items: center;
    color: white;
    font-family: LustTextItalic, serif;
    font-size: 16px;
    line-height: 1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.tiles_wrap {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    border-top: 1px solid @fl_border;
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 8px 16px 8px 8px;
}

// Each file's row: the tile plus its trailing divider, kept together so the
// 16px wrap gap sits above and below the pair (matches Figma's Section frame).
.tile_group {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.tile {
    display: flex;
    flex-direction: column;
    gap: 24px;
    padding: 16px;
    border-radius: 8px;
    cursor: pointer;
    transition: background-color 0.15s ease;

    &:hover,
    &.tile_selected {
        background-color: @fl_tile_hover_bg;
    }
}

// Full-width separator between tiles (Figma: #E3E6EB, spans the whole item).
.tile_divider {
    height: 1px;
    background-color: #e3e6eb;
    width: 100%;
    flex-shrink: 0;
}

.tile_title_row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    width: 100%;
}

.tile_title {
    font-family: 'Hanken Grotesk', sans-serif;
    font-size: 16px;
    font-weight: 700;
    line-height: 1.5;
    color: @fl_tile_title;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.tile_actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    flex-shrink: 0;
}

.tile_date {
    font-family: 'Hanken Grotesk', sans-serif;
    font-size: 12px;
    font-weight: 400;
    line-height: 1.5;
    color: @fl_text;
}

.tile_overview {
    font-family: 'Hanken Grotesk', sans-serif;
    font-size: 16px;
    font-weight: 400;
    line-height: 1.5;
    color: #000000;
    overflow-wrap: break-word;
    white-space: pre-wrap;
}

// Secondary button used for View / Delete / Add. The selected tile promotes its
// View button to the hover treatment so the active file reads at a glance.
.btn {
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    // Figma: View 51x28, trash 32x28 — lock the height so button UA styles
    // can't inflate it.
    height: 28px;
    padding: 4px 8px;
    margin: 0;
    border-radius: 8px;
    border: 1px solid @fl_btn_border;
    background-color: #ffffff;
    font-family: 'Hanken Grotesk', sans-serif;
    font-size: 14px;
    font-weight: 500;
    line-height: 1;
    color: @fl_text;
    cursor: pointer;
    white-space: nowrap;
    transition:
        background-color 0.15s ease,
        border-color 0.15s ease;

    &:hover:not(:disabled) {
        background-color: @fl_btn_hover_bg;
        border-color: @fl_btn_hover_border;
        font-weight: 600;
    }

    &:disabled {
        opacity: 0.5;
        cursor: default;
    }

    img {
        width: 16px;
        height: 16px;
        flex-shrink: 0;
    }
}

.btn_icon_only {
    padding: 4px 8px;
    justify-content: center;
}

.btn_active {
    background-color: @fl_btn_hover_bg;
    border-color: @fl_btn_hover_border;
    font-weight: 600;
}

// The header "Add" control is a Dropzone-backed Button, so the base styles need
// to win over Bootstrap's.
.uploadButton {
    // react-bootstrap Button: override .btn's inline-block/line-height so the
    // icon and label center as one flex row (Figma: 70x28, gap 4, px8 py4).
    box-sizing: border-box !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    // The header is a stretch flex row; pin the button to vertical center.
    align-self: center !important;
    gap: 4px !important;
    height: 28px !important;
    padding: 4px 8px !important;
    margin: 0 !important;
    border: 1px solid @fl_btn_border !important;
    background-color: #ffffff !important;
    border-radius: 8px !important;
    flex-shrink: 0;

    font-family: 'Hanken Grotesk', sans-serif !important;
    font-size: 14px !important;
    font-weight: 500 !important;
    line-height: 1 !important;
    color: @fl_text !important;
    white-space: nowrap;

    img {
        display: block;
        width: 20px;
        height: 20px;
        margin: 0 !important;
        flex-shrink: 0;
    }

    &:hover,
    &:focus,
    &:active {
        background-color: @fl_btn_hover_bg !important;
        border-color: @fl_btn_hover_border !important;
        color: @fl_text !important;
        box-shadow: none !important;
    }
}

.treeEmptyText {
    text-align: center;
    padding: 24px 16px;
    color: @fl_text_muted;
    font-family: 'Hanken Grotesk', sans-serif;
    font-size: 15px;
    font-weight: 500;
}

.processing_cell {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 50px;
    height: 50px;
    flex-shrink: 0;
}
